import React from 'react'
import { MdAdd } from 'react-icons/md'
import { formatOrdinalDate, getDayName } from '@/utils/deliveryDays'

/**
 * A product the customer already takes on another day, offered as a quick add
 * onto an empty delivery day (DF-343). Clicking Add opens a short wizard for
 * the quantity and then the start date.
 */
export interface QuickAddOption {
  productId: string
  name: string
  image: string | null
  /** VAT-inclusive price, for display, matching the delivery cards. */
  unitPrice: number
  /** Net price, sent to the backend as unit_price (which must stay net). */
  netPrice: number
  chargebeeId: string
}

/** A product already added to this day that has not started yet. */
export interface QuickAddUpcoming {
  productId: string
  name: string
  image: string | null
  /** YYYY-MM-DD, the first delivery of this product on this day. */
  startDate: string
}

/** What an empty card for one delivery day can offer and show. */
export interface QuickAddDay {
  options: QuickAddOption[]
  upcoming: QuickAddUpcoming[]
}

interface Props {
  /** The empty delivery day (0 = Sunday ... 6 = Saturday). */
  day: number
  /** The dated heading for the card, e.g. "Thursday 24th September". */
  dateLabel: string
  /** Products from the customer's other day(s), available on this day. */
  options: QuickAddOption[]
  /** Products on this day that start after this card's date, soonest first. */
  upcoming?: QuickAddUpcoming[]
  /** Open the quick-add wizard for this product on this day. */
  onQuickAdd: (option: QuickAddOption, day: number) => void
  disabled?: boolean
}

/**
 * DF-343: an empty but available delivery day, shown as a dated card in My
 * Deliveries. Below the "nothing scheduled" message it lists the products the
 * customer takes on their other day(s) as small thumbnails, each with an Add
 * button, so they can mirror their order onto this day. The name shows on
 * hover and in the wizard. Once something is added but has not started yet,
 * the card just says when this day's deliveries begin.
 */
const QuickAddEmptyDay: React.FC<Props> = ({ day, dateLabel, options, upcoming = [], onQuickAdd, disabled = false }) => {
  const dayName = getDayName(day)
  const firstStart = upcoming[0]?.startDate

  return (
    <div className="sun-empty-delivery">
      <h3 className="sun-empty-delivery-date">{dateLabel}</h3>
      {firstStart ? (
        <div className="sun-quickadd-upcoming">
          {upcoming.map(item => (
            item.image
              ? <img key={item.productId} className="sun-quickadd-thumb" src={item.image} alt={item.name} title={item.name} />
              : <span key={item.productId} className="sun-quickadd-thumb sun-quickadd-thumb--placeholder" aria-label={item.name} title={item.name} />
          ))}
          <p className="sun-empty-delivery-message">
            Your {dayName} deliveries start on {formatOrdinalDate(firstStart)}.
          </p>
        </div>
      ) : (
        <p className="sun-empty-delivery-message">You don't have any products scheduled for this delivery.</p>
      )}

      {/* Only offered while the day has nothing lined up; once something is
          added, the card just says when it starts. */}
      {!firstStart && options.length > 0 && (
        <>
          <p className="sun-quickadd-lead">
            Add what you already get on your other day to every {dayName} delivery:
          </p>

          <ul className="sun-quickadd-list">
            {options.map(option => (
              <li key={option.productId} className="sun-quickadd-row" title={option.name}>
                {option.image
                  ? <img className="sun-quickadd-thumb" src={option.image} alt="" />
                  : <span className="sun-quickadd-thumb sun-quickadd-thumb--placeholder" aria-hidden="true" />}
                <button
                  type="button"
                  className="sun-skip-link"
                  onClick={() => onQuickAdd(option, day)}
                  disabled={disabled}
                  aria-label={`Add ${option.name} to every ${dayName} delivery`}
                >
                  <MdAdd aria-hidden="true" /> Add
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

export default QuickAddEmptyDay
